<script lang="ts">
  import BaseStep from "./BaseStep.svelte";
  import SharedHeading from "../SharedHeading.svelte";
  import { getCopy } from "../../lib/stores/copy";
  import Button from "../Button.svelte";
  import {
    initiateEmailVerification,
    isEmailVerified,
    pollEmailVerificationStatus
  } from "../../lib/helpers/steps/verifyEmail";
  import Typography from "../Typography.svelte";
  import { TypographyType } from "../../lib/constants/typographyMap";
  import { onDestroy, onMount } from "svelte";
  import { navigateTo } from "../../lib/routing";
  import { NavigationType } from "../../lib/stores/session";
  import LoadingScreen from "../LoadingScreen.svelte";
  import { isDemoMode } from "../../lib/stores/mode";
  import CustomHtml from "../CustomHTML.svelte";

  export let currentRoute;

  let hasSentEmail = false;
  let isLoading = false;
  let email = "";
  let emailSent: string;
  let apiErrorOccurred = false;
  let isPreparing = true;

  $: copy = $getCopy("verifyEmail") as Record<string, string>;
  $: generalCopy = $getCopy("general", { basePath: "" }) as Record<string, string>;

  const onEmailVerified = () => navigateTo(NavigationType.Next);

  let poller: { stopPolling: boolean } | undefined;

  onMount(async () => {
    try {
      const isUserEmailVerified = await isEmailVerified();
      if (isUserEmailVerified) {
        onEmailVerified();
      }
    } catch (error) {
      // will do nothing
    }
    isPreparing = false;
  });

  const requestEmailVerification = async () => {
    if (isDemoMode()) {
      onEmailVerified();
      return;
    }

    try {
      isLoading = true;
      apiErrorOccurred = false;
      email = await initiateEmailVerification();
      hasSentEmail = true;
      emailSent = copy?.emailSent?.replace("{email}", email) || "";
    } catch (error) {
      apiErrorOccurred = true;
    }

    if (!apiErrorOccurred) {
      poller = pollEmailVerificationStatus(onEmailVerified);
    }

    isLoading = false;
  };

  onDestroy(() => {
    if (poller) {
      poller.stopPolling = true;
    }
  });
</script>

<BaseStep {currentRoute}>
  <svelte:fragment slot="content">
    {#if isPreparing}
      <LoadingScreen />
    {:else}
      <SharedHeading headerCopy={copy.headerCopy} subHeaderCopy={hasSentEmail ? "" : copy.subHeaderCopy} />

      {#if !hasSentEmail}
        {#if apiErrorOccurred}
          <div class="mt-6">
            <Typography mobile={TypographyType.Body2Regular} desktop={TypographyType.Body2Regular} --font-color="red">
              {generalCopy?.fatalErrorCopy}
            </Typography>
          </div>
        {/if}
        <Button
          isBusy={isLoading}
          disabled={isLoading}
          class="mt-6 w-full lg:w-fit"
          primary
          on:click={requestEmailVerification}
          aria-role="verify email">
          {copy.verifyButtonCopy}
        </Button>
      {:else}
        <strong class="mt-4 text-center lg:text-justify">
          <Typography
            mobile={TypographyType.Body1Medium}
            desktop={TypographyType.Headline2}
            --font-color="var(--subheader-font-color)">
            {emailSent}
          </Typography>
        </strong>
      {/if}

      <CustomHtml customHtml={copy.customHtml} />
    {/if}
  </svelte:fragment>
</BaseStep>
